import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LogIn, Eye, EyeOff, Shield } from "lucide-react";
import { toast } from "sonner";

function formatApiError(detail) {
  if (!detail) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hubLoginUrl, setHubLoginUrl] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/config")
      .then(({ data }) => setHubLoginUrl(data?.hub_login_url || ""))
      .catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    if (!email || !password) {
      setError("Please enter email and password");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await login(email, password);
      toast.success(`Welcome back, ${data.name}!`);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-4">

      <div className="w-full max-w-md">
        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="mb-8 text-center">
            <img src="/cargodash-logo.svg" alt="CargoDash" className="h-10 mx-auto mb-2" />
            <p className="text-slate-400 text-sm">Welcome to <span className="italic text-teal-600">Skyworth Job Card System</span></p>
          </div>

          {hubLoginUrl && (
            <div className="mb-5">
              <a
                href={hubLoginUrl}
                data-testid="hub-sso-btn"
                target="_top"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold h-11 rounded-full text-sm transition-all"
              >
                <Shield size={16} /> Open BlackScott Hub
              </a>
              <p className="text-[10px] text-slate-400 text-center mt-2 leading-snug">
                Hub users: sign in there, then click the <strong>Skyworth Job Cards</strong> tile.
              </p>
              <div className="flex items-center gap-3 my-4 text-[10px] uppercase tracking-[0.2em] text-slate-400">
                <div className="flex-1 h-px bg-slate-200" />
                or direct sign-in
                <div className="flex-1 h-px bg-slate-200" />
              </div>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-500">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="bg-white border-slate-200 text-slate-800 h-11 rounded-lg"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-500">Password</Label>
              <div className="relative">
                <Input
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="bg-white border-slate-200 text-slate-800 pr-10 h-11 rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <p data-testid="login-error" className="text-red-400 text-sm bg-red-500/10 border border-red-200 rounded-xl p-2">
                {error}
              </p>
            )}

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold h-11 rounded-full text-base"
            >
              {loading ? "Signing in..." : <><LogIn size={16} className="mr-2" /> Sign In</>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
